import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateTask, useUploadAttachments } from '../api/tasks';

interface Props {
  projectSlug: string;
  onClose: () => void;
}

const MAX_FILES = 5;
const MAX_BYTES_PER_FILE = 25 * 1024 * 1024;

/**
 * Two-phase submit: first POST /tasks (returns task id), then POST
 * /tasks/:id/attachments with the multipart files. We don't navigate
 * until BOTH succeed so the user sees a single "Submitting…" state.
 *
 * Why two phases instead of multipart create: lets backend keep its
 * existing JSON validation on the create endpoint, and the upload
 * endpoint already has correct per-file size + count + path-traversal
 * guards. Two roundtrips is fine for a manual user action.
 */
export default function NewTaskForm({ projectSlug, onClose }: Props) {
  const [prompt, setPrompt] = useState('');
  const [baseBranch, setBaseBranch] = useState('');
  const [autopilot, setAutopilot] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ─── Advanced: per-task v2 bounds (P0). Empty = inherit project config. ──
  // These map to a partial config.jsonc the worker forwards to v2 as
  // --config-override. They're the "bound a walk-away run" controls the
  // panel↔v2 coverage audit flagged as missing (cost/runtime/blast-radius/
  // drift + how boldly autopilot self-approves).
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [maxCost, setMaxCost] = useState('');
  const [maxRuntime, setMaxRuntime] = useState('');
  const [blastRadius, setBlastRadius] = useState('');
  const [driftPolicy, setDriftPolicy] = useState('');
  const [autoApproveMaxRisk, setAutoApproveMaxRisk] = useState('');
  const [autoApproveUnlessDangerous, setAutoApproveUnlessDangerous] = useState('');

  /** Assemble a partial config_override from only the fields the user set. */
  const buildConfigOverride = (): Record<string, unknown> | undefined => {
    const override: Record<string, unknown> = {};
    const limits: Record<string, number> = {};
    const cost = Number(maxCost);
    if (maxCost.trim() && Number.isFinite(cost) && cost > 0) limits.max_task_cost_usd = cost;
    const runtime = Number(maxRuntime);
    if (maxRuntime.trim() && Number.isFinite(runtime) && runtime > 0) {
      limits.max_runtime_minutes = Math.round(runtime);
    }
    if (Object.keys(limits).length) override.limits = limits;
    if (blastRadius) override.permissions = { blast_radius_policy: blastRadius };
    if (driftPolicy) override.drift = { policy: driftPolicy };
    const ap: Record<string, unknown> = {};
    if (autoApproveMaxRisk) ap.auto_approve_max_risk = autoApproveMaxRisk;
    if (autoApproveUnlessDangerous) {
      ap.auto_approve_unless_dangerous = autoApproveUnlessDangerous === 'yes';
    }
    if (Object.keys(ap).length) override.autopilot = ap;
    return Object.keys(override).length ? override : undefined;
  };

  const createTask = useCreateTask(projectSlug);
  const navigate = useNavigate();
  // Attachments hook needs a taskId — we don't have one yet on the
  // form. Use a placeholder string; the hook is only invoked AFTER
  // createTask succeeds, so the closure captures the real id at call
  // time via the second arg pattern below.
  const upload = useUploadAttachments('pending', projectSlug);

  const totalBytes = files.reduce((n, f) => n + f.size, 0);

  const addFiles = (incoming: FileList | File[]): void => {
    setLocalError(null);
    const arr = Array.from(incoming);
    if (files.length + arr.length > MAX_FILES) {
      setLocalError(`Max ${MAX_FILES} files per task (have ${files.length}, trying to add ${arr.length}).`);
      return;
    }
    for (const f of arr) {
      if (f.size > MAX_BYTES_PER_FILE) {
        setLocalError(`${f.name} is ${(f.size / 1024 / 1024).toFixed(1)} MB — max 25 MB per file.`);
        return;
      }
    }
    setFiles((prev) => [...prev, ...arr]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx: number): void => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (): Promise<void> => {
    if (!prompt.trim()) return;
    setLocalError(null);
    try {
      const configOverride = buildConfigOverride();
      const task = await createTask.mutateAsync({
        prompt: prompt.trim(),
        ...(baseBranch.trim() ? { base_branch: baseBranch.trim() } : {}),
        ...(autopilot ? { autopilot: true } : {}),
        ...(configOverride ? { config_override: configOverride } : {}),
      });
      if (files.length > 0) {
        // Re-issue the mutation against the now-known task id.
        const res = await fetch(`/api/tasks/${task.id}/attachments`, {
          method: 'POST',
          credentials: 'include',
          body: (() => {
            const form = new FormData();
            for (const f of files) form.append('files', f, f.name);
            return form;
          })(),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
            message?: string;
          };
          throw new Error(
            `Task was created but file upload failed: ${body.message ?? body.error ?? `HTTP ${res.status}`}. ` +
              `You can still attach files on the task page before approving.`,
          );
        }
      }
      onClose();
      navigate(`/p/${projectSlug}/tasks/${task.id}`);
    } catch (err) {
      setLocalError((err as Error).message);
    }
  };

  const pending = createTask.isPending || upload.isPending;

  return (
    <Modal onClose={onClose} title="New task">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="block text-sm font-medium text-slate-700 mb-1">
          What should the AI do?
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={6}
          autoFocus
          placeholder="Describe the change in plain English. Include any rules in 'Remember: …' format and they'll be saved as project rules."
          className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />

        <label className="mt-3 block text-sm font-medium text-slate-700 mb-1">
          Continue on existing branch <span className="text-slate-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={baseBranch}
          onChange={(e) => setBaseBranch(e.target.value)}
          placeholder="leave blank to create a new branch — or type part of a branch name"
          className="w-full rounded border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        <p className="mt-1 text-xs text-slate-500">
          We&apos;ll match this against local and <code>origin/*</code> branches. Exact name wins;
          otherwise a unique substring is enough.
        </p>

        {/* ─── Auto-pilot ───────────────────────────────────── */}
        <label className="mt-3 flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={autopilot}
            onChange={(e) => setAutopilot(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-sm">
            <span className="font-medium text-slate-700">🤖 Auto-pilot</span>
            <span className="block text-xs text-slate-500">
              Run unattended — the pipeline answers its own clarifying questions
              (picking recommended options, else a resolver agent that inspects
              the code) and auto-approves low/medium-risk plans. It still asks
              you only when it isn&apos;t confident or the risk is high.
            </span>
          </span>
        </label>

        {/* ─── Attachments ──────────────────────────────────── */}
        <label className="mt-3 block text-sm font-medium text-slate-700 mb-1">
          Attachments <span className="text-slate-400 font-normal">(optional — PDF, image, doc, code snippet…)</span>
        </label>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
          }}
          className="rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-sm text-slate-600"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
            }}
            className="block text-sm text-slate-600 file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
          />
          <p className="mt-1 text-xs text-slate-500">
            Drag &amp; drop or click. Max {MAX_FILES} files, 25 MB each. The
            AI will Read these when working on the task.
          </p>
          {files.length > 0 && (
            <ul className="mt-2 space-y-1">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded px-2 py-1 text-xs"
                >
                  <span className="truncate">
                    📎 <span className="font-mono">{f.name}</span>{' '}
                    <span className="text-slate-500">({formatBytes(f.size)})</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="text-slate-400 hover:text-red-600"
                    aria-label={`Remove ${f.name}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          {files.length > 0 && (
            <p className="mt-1 text-xs text-slate-500">
              Total: {formatBytes(totalBytes)} across {files.length} file{files.length === 1 ? '' : 's'}.
            </p>
          )}
        </div>

        {/* ─── Advanced: limits, safety & autopilot policy (v2 bounds) ─── */}
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          aria-expanded={showAdvanced}
          className="mt-3 text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          {showAdvanced ? '▾' : '▸'} Advanced — limits, safety &amp; autopilot policy
        </button>
        {showAdvanced && (
          <div className="mt-2 space-y-3 rounded border border-slate-200 bg-slate-50 px-3 py-3">
            <p className="text-xs text-slate-500">
              Leave blank / &ldquo;Project default&rdquo; to inherit the project config.
              These bound or tune <em>this task only</em>.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-medium text-slate-700">
                Max cost (USD)
                <input
                  type="number" min="1" max="1000" step="1"
                  value={maxCost}
                  onChange={(e) => setMaxCost(e.target.value)}
                  placeholder="e.g. 5"
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </label>
              <label className="text-xs font-medium text-slate-700">
                Max runtime (min)
                <input
                  type="number" min="1" max="1440" step="1"
                  value={maxRuntime}
                  onChange={(e) => setMaxRuntime(e.target.value)}
                  placeholder="e.g. 30"
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </label>
              <label className="text-xs font-medium text-slate-700">
                Blast-radius policy
                <select
                  value={blastRadius}
                  onChange={(e) => setBlastRadius(e.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Project default</option>
                  <option value="warn">warn (log risky cmds)</option>
                  <option value="enforce">enforce (block risky cmds)</option>
                  <option value="off">off</option>
                </select>
              </label>
              <label className="text-xs font-medium text-slate-700">
                Drift policy
                <select
                  value={driftPolicy}
                  onChange={(e) => setDriftPolicy(e.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Project default</option>
                  <option value="log_only">log_only</option>
                  <option value="replan_on_low">replan_on_low</option>
                  <option value="off">off</option>
                </select>
              </label>
              <label className="text-xs font-medium text-slate-700">
                Autopilot: auto-approve up to risk
                <select
                  value={autoApproveMaxRisk}
                  onChange={(e) => setAutoApproveMaxRisk(e.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Project default</option>
                  <option value="low">low</option>
                  <option value="med">med</option>
                  <option value="high">high</option>
                </select>
              </label>
              <label className="text-xs font-medium text-slate-700">
                Autopilot: approve safe-but-large
                <select
                  value={autoApproveUnlessDangerous}
                  onChange={(e) => setAutoApproveUnlessDangerous(e.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Project default</option>
                  <option value="yes">yes (smart gate)</option>
                  <option value="no">no (strict cap)</option>
                </select>
              </label>
            </div>
            <p className="text-xs text-slate-400">
              Autopilot-policy fields take effect only when Auto-pilot is on. An out-of-range
              value is rejected by the pipeline with a clear error rather than silently ignored.
            </p>
          </div>
        )}

        <p className="mt-3 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
          Every task pauses at the plan-approval gate. After you approve,
          the pipeline runs straight through to commit + push + PR
          without further interruption (a Jr-flagged blocking question
          can still pause for safety).
        </p>

        {(localError || createTask.error) && (
          <div className="mt-3 text-sm text-red-700">
            {localError ?? (createTask.error as Error).message}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!prompt.trim() || pending}
            className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {pending
              ? files.length > 0
                ? 'Uploading…'
                : 'Submitting…'
              : files.length > 0
                ? `Create task + ${files.length} file${files.length === 1 ? '' : 's'}`
                : 'Create task'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function Modal({
  onClose,
  title,
  children,
}: {
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-xl w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-3">{title}</h2>
        {children}
      </div>
    </div>
  );
}
